import {
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react'

import useAmplitude, { EVENT_NAMES } from '@/hooks/use-amplitude'
import { useSettings } from '@/hooks/use-settings'
import {
  RENEC,
  RENEC_MINT_ADDRESS,
  RENGN_MINT_ADDRESS,
  REUSD,
  REUSD_MINT_ADDRESS,
  REVND_MINT_ADDRESS,
  type SwapTokenInfo,
  useTokens,
} from '@/hooks/use-tokens'
import { LanguageContext } from '@/providers/language'

import useTokenMintAddresses from './use-token-mint-addresses'

export type PoolInfo = {
  volumesIn24h?: number;
  tokenAName?: string;
  tokenASymbol?: string;
  tokenALogoUrl?: string;
  tokenBName?: string;
  tokenBSymbol?: string;
  tokenBLogoUrl?: string;
}

export type SwapContextType = {
  inputMintAddress: string,
  setInputMintAddress: (mintAddress: string) => void,
  outputMintAddress: string,
  setOutputMintAddress: (mintAddress: string) => void,
  switchMintAddresses: () => void,
  tokenIn?: SwapTokenInfo,
  tokenOut?: SwapTokenInfo,
  isLoading: boolean,
}

type SwapContextProps = HocProps & {
  initMintAddresses?: [string, string]
}

const defaultTokenInfoA = {
  nickName: 'tokenA',
  chainId: 101,
  address: RENEC_MINT_ADDRESS,
  name: RENEC,
  decimals: 9,
  symbol: RENEC,
  logoURI: 'https://remitano.com/coins/renec.png',
}

export const defaultTokenInfoB = {
  nickName: 'tokenA',
  chainId: 101,
  address: REUSD_MINT_ADDRESS,
  name: REUSD,
  decimals: 9,
  symbol: REUSD,
  logoURI: 'https://raw.githubusercontent.com/renec-chain/rpl-token-registry/master/assets/mainnet/4Q89182juiadeFgGw3fupnrwnnDmBhf7e7fHWxnUP3S3/logo.png',
}

const initContextState: SwapContextType = {
  inputMintAddress: REUSD,
  setInputMintAddress: () => null,
  outputMintAddress: REUSD,
  setOutputMintAddress: () => null,
  switchMintAddresses: () => null,
  tokenIn: defaultTokenInfoA,
  tokenOut: defaultTokenInfoB,
  isLoading: false,
}

const SwapContext = createContext<SwapContextType>(initContextState)

const DEFAULT_MINT_ADDRESSES_BY_LANG_MAP: {[key: string]: [string, string]} = {
  vi: ['', REVND_MINT_ADDRESS],
  vn: ['', REVND_MINT_ADDRESS],
  ng: ['', RENGN_MINT_ADDRESS],
  en: ['', RENEC_MINT_ADDRESS],
}

export const SwapProvider = ({
  children,
}: SwapContextProps) => {
  const { isLoading: isLoadingSettings } = useSettings()
  const { getTokenInfo, isLoading: isLoadingTokens } = useTokens()
  const amplitude = useAmplitude()
  const { isoCode } = useContext(LanguageContext)

  const initMintAddresses = useMemo(
    () =>
      DEFAULT_MINT_ADDRESSES_BY_LANG_MAP[isoCode?.toLowerCase() || 'en'] ??
      DEFAULT_MINT_ADDRESSES_BY_LANG_MAP['en'],
    [isoCode],
  )

  const { mintAddresses, setMintAddresses } = useTokenMintAddresses(initMintAddresses)

  const [inputMintAddress, outputMintAddress] = useMemo(() => mintAddresses, [mintAddresses])

  const [tokenIn, tokenOut] = useMemo(() => [
    inputMintAddress ? getTokenInfo(inputMintAddress) : undefined,
    outputMintAddress ? getTokenInfo(outputMintAddress) : undefined,
  ], [getTokenInfo, inputMintAddress, outputMintAddress])

  const switchMintAddresses = useCallback(() => {
    amplitude.track(EVENT_NAMES.swap_switch_token, {
      from_token: tokenOut?.symbol,
      to_token: tokenIn?.symbol,
    })

    setMintAddresses(([a, b]) => [b, a])
  }, [amplitude, tokenIn, tokenOut, setMintAddresses])

  const setInputMintAddress = useCallback((mintAddress: string) => {
    setMintAddresses(([a, b]) => [mintAddress, mintAddress === b ? a : b])

    const token = getTokenInfo(mintAddress)
    amplitude.track(EVENT_NAMES.swap_select_from_token, { token_name: token?.symbol })
  }, [amplitude, getTokenInfo, setMintAddresses])

  const setOutputMintAddress = useCallback((mintAddress: string) => {
    setMintAddresses(([a, b]) => [mintAddress === a ? b : a, mintAddress])

    const token = getTokenInfo(mintAddress)
    amplitude.track(EVENT_NAMES.swap_select_to_token, { token_name: token?.symbol })
  }, [amplitude, getTokenInfo, setMintAddresses])

  const contextValue = useMemo(() => {
    return {
      inputMintAddress,
      setInputMintAddress,
      outputMintAddress,
      setOutputMintAddress,
      switchMintAddresses,
      tokenIn,
      tokenOut,
      isLoading: isLoadingSettings || isLoadingTokens,
    }
  }, [
    inputMintAddress,
    setInputMintAddress,
    outputMintAddress,
    setOutputMintAddress,
    switchMintAddresses,
    tokenIn,
    tokenOut,
    isLoadingSettings,
    isLoadingTokens,
  ])

  return (
    <SwapContext.Provider value={contextValue}>
      {children}
    </SwapContext.Provider>
  )
}

SwapContext.displayName = 'SwapContext'
export default SwapContext
